import React from 'react'
import moment from 'moment'
import {
  Card, CardBody, CardTitle, Row, Col, Spinner, Container,
} from 'reactstrap'
import { useQuery, gql } from '@apollo/client'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Bar } from 'react-chartjs-2'
import * as chartjs from 'chart.js'
import { respondentsToBarChartData, ChartColors, ChartLabels } from 'components/charts/respondentCharts'
import { Query } from 'generated/types'
import { useFilters } from './FiltersContext'

export const RESPONDENT_COUNTS = gql`
  query RespondentsCount($tags: [String!], $questionSlug: String!, $startDate: DateTime!, $endDate: DateTime!) {
    questionBySlug(slug: $questionSlug) {
      id
      slug
      text
      respondentCounts(filters: { tags: $tags, startDate: $startDate, endDate: $endDate }) {
        totalRespondents
        respondentCounts {
          option {
            id
            slug
            text
          }
          respondents
        }
      }
    }
  }
`

interface ResponsesChartProps {
  questionSlug: string
  startDate: moment.Moment
  endDate: moment.Moment
  chartTitle: string
  chartColors: ChartColors
  chartLabels?: ChartLabels
  optionOrder?: string[]
}

const ResponsesChart: React.FC<ResponsesChartProps> = (props) => {
  const [filters] = useFilters()

  const respondents = useQuery<Query>(RESPONDENT_COUNTS, {
    variables: {
      questionSlug: props.questionSlug,
      tags: filters.tags,
      startDate: props.startDate,
      endDate: props.endDate,
    },
  })

  if (respondents.loading) {
    return <Spinner />
  }

  if (respondents.error) {
    console.error('error loading respondents:', respondents.error)

    return (
      <>
        <div className="d-flex justify-content-center">
          <FontAwesomeIcon
            icon="exclamation-triangle"
            color={props.chartColors.error}
            transform="grow-30"
            className="mt-3"
          />
        </div>
        <div className="mt-4 text-center fs--1 text-white">
          <p>There was an error loading responses</p>
        </div>
      </>
    )
  }

  const chartOptions: chartjs.ChartOptions = {
    tooltips: {
      callbacks: {
        title: () => '', // makes title blank rather than showing 'undefined'
      },
    },
    legend: {
      position: 'right',
      labels: {
        fontSize: 12,
        boxWidth: 8,
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            min: 0,
            max: 100,
            maxTicksLimit: 5,
            callback(value: number) {
              return `${value}%`
            },
          },
          scaleLabel: {
            display: false,
          },
        },
      ],
    },
  }

  const rc = respondents.data!.questionBySlug.respondentCounts
  const chartData = respondentsToBarChartData(rc, props.chartColors, props.chartLabels, props.optionOrder)

  return (
    <Card className="mb-3">
      <CardBody>
        <CardTitle>
          {props.chartTitle}
          <span className="ml-3 fs--1">
            {props.startDate.format('M/D')} - {props.endDate.clone().subtract(1, 'day').format('M/D')}
          </span>
        </CardTitle>
        <Bar height={125} data={chartData} options={chartOptions} />
      </CardBody>
    </Card>
  )
}

interface RespondentsPercentageBarChartProps {
  questionSlug: string
  title: string
  leftChartTitle: string
  rightChartTitle: string
  leftChartStart: moment.Moment
  leftChartEnd: moment.Moment
  rightChartStart: moment.Moment
  rightChartEnd: moment.Moment
  chartColors: ChartColors
  chartLabels?: ChartLabels
  optionOrder?: string[]
  bgClass?: string
  titleClass?: string
}

const RespondentsPercentageBarChart: React.FC<RespondentsPercentageBarChartProps> = (props) => {
  const bgClass = props.bgClass ? props.bgClass : 'bg-blvl-gradient-blue'
  const titleClass = props.titleClass ? props.titleClass : ''

  return (
    <Card className="mb-3">
      <CardBody className={['rounded-soft', bgClass].join(' ')}>
        <CardTitle>
          <h4 className={titleClass}>{props.title}</h4>
        </CardTitle>
        <Container>
          <Row>
            <Col md="12" lg="6">
              <ResponsesChart
                questionSlug={props.questionSlug}
                startDate={props.leftChartStart}
                endDate={props.leftChartEnd}
                chartTitle={props.leftChartTitle}
                chartColors={props.chartColors}
                chartLabels={props.chartLabels}
                optionOrder={props.optionOrder}
              />
            </Col>
            <Col md="12" lg="6">
              <ResponsesChart
                questionSlug={props.questionSlug}
                startDate={props.rightChartStart}
                endDate={props.rightChartEnd}
                chartTitle={props.rightChartTitle}
                chartColors={props.chartColors}
                chartLabels={props.chartLabels}
                optionOrder={props.optionOrder}
              />
            </Col>
          </Row>
        </Container>
      </CardBody>
    </Card>
  )
}

export default RespondentsPercentageBarChart
